/**
 * Hooks de las preferencias de correo (TanStack Query).
 *
 * Claves de la caché:
 *  - `['correos', 'yo']`: las propias (Mi cuenta).
 *  - `['correos', id]`: las de otra persona (diálogo de Personas, solo ADMIN).
 *  - `['correos', 'personas']`: los avisos de todas, por id (columna «Correos» de Personas, una sola petición).
 *
 * Guardar es optimista y **en serie** por persona (`scope`), para que dos cambios seguidos no se crucen:
 *  - Cada cambio es un delta (`{ clave, valor }`): se pinta al instante en `onMutate` y, cuando le toca salir, el PUT se
 *    arma con lo que hay en la caché en ese momento más su delta. Si un cambio anterior falló, la caché ya volvió a lo
 *    del servidor y el siguiente no lo arrastra.
 *  - Si falla, no se vuelve a una copia (podría pisar un cambio posterior): se pide de nuevo al servidor.
 *  - Al terminar el último guardado de la fila se vuelve a pedir, así lo que se ve es lo que quedó en la base.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { AvisosCorreo, PreferenciasCorreo, TipoAviso } from '@cdtec-backlog/contrato';
import { guardarCorreos, listarAvisosDePersonas, obtenerCorreos, type DestinoCorreos } from './api';

export const claveCorreos = (destino: DestinoCorreos) => ['correos', destino] as const;
export const CLAVE_AVISOS_PERSONAS = ['correos', 'personas'] as const;

/** Un cambio de un interruptor. */
export interface CambioAviso {
  clave: TipoAviso | 'propios';
  valor: boolean;
}

export function useCorreos(destino: DestinoCorreos) {
  return useQuery({
    queryKey: claveCorreos(destino),
    queryFn: () => obtenerCorreos(destino),
  });
}

/** Avisos de todas las personas (solo ADMIN; la API responde 403 a los demás). */
export function useAvisosDePersonas(activo = true) {
  return useQuery({
    queryKey: CLAVE_AVISOS_PERSONAS,
    queryFn: listarAvisosDePersonas,
    enabled: activo,
  });
}

const conCambio = (
  previas: PreferenciasCorreo | undefined,
  cambio: CambioAviso,
): PreferenciasCorreo | undefined =>
  previas ? { ...previas, avisos: { ...previas.avisos, [cambio.clave]: cambio.valor } } : previas;

/** Lo que dependía de esta persona: su lista, «yo» si es uno mismo y la columna de Personas. */
function invalidarRelacionadas(cliente: QueryClient, destino: DestinoCorreos, yoId?: number) {
  const claves: (readonly unknown[])[] = [claveCorreos(destino), CLAVE_AVISOS_PERSONAS];
  if (destino === 'yo' && yoId !== undefined) claves.push(claveCorreos(yoId));
  if (destino !== 'yo' && destino === yoId) claves.push(claveCorreos('yo'));
  return Promise.all(
    claves.map((queryKey) => cliente.invalidateQueries({ queryKey, exact: true })),
  );
}

/**
 * @param yoId id de quien usa la web: si un ADMIN se edita a sí mismo desde Personas, también se refresca Mi cuenta (y
 *   al revés).
 */
export function useGuardarCorreos(destino: DestinoCorreos, yoId?: number) {
  const cliente = useQueryClient();
  const clave = claveCorreos(destino);
  const mutationKey = ['guardar-correos', destino] as const;
  return useMutation({
    mutationKey,
    // Los guardados de una misma persona salen de a uno, en el orden en que se hicieron.
    scope: { id: `correos-${destino}` },
    mutationFn: (cambio: CambioAviso) => {
      const actuales = cliente.getQueryData<PreferenciasCorreo>(clave);
      if (!actuales) return Promise.reject(new Error('Sin preferencias cargadas'));
      const avisos: AvisosCorreo = { ...actuales.avisos, [cambio.clave]: cambio.valor };
      // Un refresco entre medio pudo traer lo del servidor: el cambio se vuelve a pintar antes de enviarlo.
      cliente.setQueryData<PreferenciasCorreo>(clave, { ...actuales, avisos });
      return guardarCorreos(destino, avisos);
    },
    onMutate: async (cambio) => {
      // Que un refresco en vuelo no pise el cambio optimista.
      await cliente.cancelQueries({ queryKey: clave, exact: true });
      cliente.setQueryData<PreferenciasCorreo>(clave, (previas) => conCambio(previas, cambio));
    },
    onError: async () => {
      // Lo del servidor manda (no una copia vieja). Se espera la respuesta: así el siguiente cambio en cola parte de ahí.
      await cliente.invalidateQueries({ queryKey: clave, exact: true });
    },
    onSettled: async () => {
      // `onSettled` corre con esta mutación aún pendiente: 1 = es la última de la fila.
      if (cliente.isMutating({ mutationKey }) === 1) {
        await invalidarRelacionadas(cliente, destino, yoId);
      }
    },
  });
}
